import { Trash2 } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { formatEuro } from "../gulaConstants";
import { Field, SimpleSelect } from "../SimpleSelect";
import {
  INCOME_METHODS,
  VAT_RATES,
  parseAmount,
  round2,
  splitVatIncluded,
  type AccIncome,
  type IncomeMethod,
} from "./accounting";
import { AccountingFileField, type StoredFile } from "./AccountingFileField";
import { removeAccountingFile } from "./useAccountingData";

const today = () => new Date().toLocaleDateString("en-CA");

/** Income that does not come through Stripe: card terminal, financing, transfer… */
export const IncomeDialog = ({
  open,
  onOpenChange,
  income,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  income?: AccIncome | null;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [day, setDay] = useState(today());
  const [customer, setCustomer] = useState("");
  const [taxId, setTaxId] = useState("");
  const [concept, setConcept] = useState("");
  const [method, setMethod] = useState<IncomeMethod>("card_terminal");
  const [total, setTotal] = useState("");
  const [vatRate, setVatRate] = useState(21);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<StoredFile>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const originalFile: StoredFile = useMemo(
    () =>
      income?.file_path
        ? { path: income.file_path, name: income.file_name ?? "Justificante" }
        : null,
    [income],
  );

  useEffect(() => {
    if (!open) return;
    setDay(income?.income_date ?? today());
    setCustomer(income?.customer_name ?? "");
    setTaxId(income?.customer_tax_id ?? "");
    setConcept(income?.concept ?? "");
    setMethod(income?.method ?? "card_terminal");
    setTotal(
      income ? String(round2(Number(income.total))).replace(".", ",") : "",
    );
    setVatRate(Number(income?.vat_rate ?? 21));
    setNotes(income?.notes ?? "");
    setFile(originalFile);
    setConfirmDelete(false);
  }, [open, income, originalFile]);

  const amounts = splitVatIncluded(parseAmount(total), vatRate);

  const close = async (saved: boolean) => {
    if (!saved && file && file.path !== originalFile?.path) {
      await removeAccountingFile(file.path).catch(() => undefined);
    }
    onOpenChange(false);
  };

  const save = async () => {
    if (!concept.trim())
      return notify("Falta el concepto", { type: "warning" });
    if (!parseAmount(total))
      return notify("Falta el importe", { type: "warning" });
    setSaving(true);
    try {
      const data = {
        income_date: day,
        customer_name: customer.trim() || null,
        customer_tax_id: taxId.trim().toUpperCase() || null,
        concept: concept.trim(),
        method,
        base_amount: amounts.base_amount,
        vat_rate: vatRate,
        vat_amount: amounts.vat_amount,
        notes: notes.trim() || null,
        file_path: file?.path ?? null,
        file_name: file?.name ?? null,
      };
      if (income) {
        await dataProvider.update("gula_acc_incomes", {
          id: income.id,
          data,
          previousData: income,
        });
      } else {
        await dataProvider.create("gula_acc_incomes", { data });
      }
      if (originalFile && originalFile.path !== file?.path) {
        await removeAccountingFile(originalFile.path).catch(() => undefined);
      }
      notify(income ? "Ingreso guardado" : "Ingreso añadido", {
        type: "success",
      });
      onSaved();
      await close(true);
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar el ingreso", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!income) return;
    if (!confirmDelete) return setConfirmDelete(true);
    setSaving(true);
    try {
      await dataProvider.delete("gula_acc_incomes", {
        id: income.id,
        previousData: income,
      });
      if (income.file_path) {
        await removeAccountingFile(income.file_path).catch(() => undefined);
      }
      notify("Ingreso borrado", { type: "info" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      notify("No se ha podido borrar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close(false))}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {income ? "Editar ingreso" : "Añadir ingreso"}
          </DialogTitle>
          <DialogDescription>
            Lo que no entra por Stripe: cobros con TPV, financiera,
            transferencia o efectivo. Lo de Stripe ya entra solo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Fecha del cobro">
            <Input
              type="date"
              value={day}
              onChange={(event) => setDay(event.target.value)}
            />
          </Field>
          <SimpleSelect
            label="Forma de cobro"
            value={method}
            options={INCOME_METHODS}
            onChange={(value) => setMethod(value as IncomeMethod)}
          />
          <Field label="Cliente">
            <Input
              value={customer}
              placeholder="Nombre del cliente"
              onChange={(event) => setCustomer(event.target.value)}
            />
          </Field>
          <Field label="NIF del cliente (si lo pide)">
            <Input
              value={taxId}
              onChange={(event) => setTaxId(event.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Concepto">
              <Input
                value={concept}
                placeholder="p. ej. Equipo BLUA grifo 3 vías"
                onChange={(event) => setConcept(event.target.value)}
              />
            </Field>
          </div>
          <Field label="Total cobrado (IVA incluido)">
            <Input
              inputMode="decimal"
              value={total}
              placeholder="0,00"
              onChange={(event) => setTotal(event.target.value)}
            />
          </Field>
          <SimpleSelect
            label="% IVA"
            value={String(vatRate)}
            options={VAT_RATES.map((rate) => ({
              value: String(rate),
              label: `${rate} %`,
            }))}
            onChange={(rate) => setVatRate(Number(rate))}
          />
          {parseAmount(total) ? (
            <p className="text-sm text-muted-foreground sm:col-span-2">
              Base {formatEuro(amounts.base_amount)} + IVA{" "}
              {formatEuro(amounts.vat_amount)}
            </p>
          ) : null}
          <div className="sm:col-span-2">
            <AccountingFileField
              label="Justificante (ticket del TPV, contrato de la financiera…)"
              value={file}
              folder="incomes"
              day={day}
              onChange={setFile}
            />
          </div>
          <div className="sm:col-span-2">
            <Textarea
              placeholder="Notas (opcional)"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {income ? (
            <Button
              type="button"
              variant={confirmDelete ? "destructive" : "ghost"}
              disabled={saving}
              onClick={remove}
            >
              <Trash2 className="h-4 w-4" />
              {confirmDelete ? "¿Borrar?" : "Borrar"}
            </Button>
          ) : null}
          <Button type="button" disabled={saving} onClick={save}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
